import React, { useState, useRef, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts'
import { MessageCircle, Users, Sparkles, Mic, TicketIcon } from 'lucide-react'
import IonIcon from '@reacticons/ionicons'
import {
  fetchAvgResponseTime,
  fetchAvgSessionTime,
  messagecount,
  reportsnewusers,
  tokenusage
} from '../../services/data'
import { useSubscription } from '../../context/subscriptionprovider'
import AccessRestrictedOverlay from '../../components/restrictoverlay'
import Loader from '../../components/loader'
import '../../styles/analytics.css'
import { useLocation, useParams } from 'react-router-dom'

const Reports = () => {
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date()
    const lastMonth = new Date()
    lastMonth.setMonth(now.getMonth() - 1)
    return [lastMonth, now]
  })

  const [startDate, endDate] = dateRange
  const [showDatePicker, setShowDatePicker] = useState(false)
  const datepickerref = useRef()
  const [newUserData, setNewUserData] = useState([])
  const [totalNewUsers, setTotalNewUsers] = useState(0)
  const [chartData, setChartData] = useState([])
  const [yearRange, setYearRange] = useState('')
  const [tokenData, setTokenData] = useState([])
  const [totalTokens, setTotalTokens] = useState({
    input: 0,
    output: 0,
    total: 0
  })
  const [avgSessionData, setAvgSessionData] = useState([])
  const [avgResponseData, setAvgResponseData] = useState([])
  const [totalAvgSession, setTotalAvgSession] = useState(0)
  const [totalAvgResponse, setTotalAvgResponse] = useState(0)
  const [totalMessagescount, setTotalMessagescount] = useState([])
  const [tempDateRange, setTempDateRange] = useState(dateRange)

  const { isRestricted, activePlan, limitedPermission } = useSubscription()
  const [loading, setLoading] = useState(true)
  const location = useLocation()
    const params = useParams()
    
      // ✅ Extract store_id from URL (params or pathname)
      const getStoreIdFromUrl = () => {
        if (params?.store_id) return params.store_id
        const pathParts = location.pathname.split('/')
        console.log(pathParts[1])
        return pathParts[1] || null
      }
    
      const storeid = getStoreIdFromUrl()
  

  const predefinedRanges = {
    '1 Month': 30,
    '6 Months': 180,
    '12 Months': 365
  }

  const generateOddTicks = max => {
    const ticks = []
    let val = 1
    while (val <= max) {
      ticks.push(val)
      val += 10
    }
    return ticks
  }

  const handleQuickSelect = days => {
    const end = new Date()
    const start = new Date()
    start.setDate(end.getDate() - days)

    const range = [start, end]
    setTempDateRange(range)
    setDateRange(range)
  }

  const fetchData = async (
    customStartDate = startDate,
    customEndDate = endDate
  ) => {
    // Wait for any async config to load
    await new Promise(resolve => setTimeout(resolve, 200))

    if (isRestricted) {
      console.warn('Bot config fetch restricted due to permission limitations.')
      return
    }

    try {
      // const store_id = localStorage.getItem('store_id')
      const store_id = storeid
      const formatDate = date => date.toISOString().split('T')[0]

      const [
        usersResponse,
        tokensResponse,
        sessionResponse,
        responseResponse,
        messagesResponse
      ] = await Promise.all([
        reportsnewusers(
          store_id,
          formatDate(customStartDate),
          formatDate(customEndDate)
        ),
        tokenusage(
          store_id,
          formatDate(customStartDate),
          formatDate(customEndDate)
        ),
        fetchAvgSessionTime(
          store_id,
          formatDate(customStartDate),
          formatDate(customEndDate)
        ),
        fetchAvgResponseTime(
          store_id,
          formatDate(customStartDate),
          formatDate(customEndDate)
        ),
        messagecount(
          store_id,
          formatDate(customStartDate),
          formatDate(customEndDate)
        )
      ])

      // USERS
      if (
        usersResponse.success &&
        usersResponse.data.total?.total_new_users > 0
      ) {
        setNewUserData(usersResponse.data.result[0])
        setTotalNewUsers(usersResponse.data.total?.total_new_users || 0)
        processChartData(usersResponse.data)
      } else {
        setNewUserData({})
        setTotalNewUsers(0)
        setChartData([])
        // processChartData({ result: [] }) // empty chart
        processChartData(usersResponse.data)
      }

      // TOKENS
      if (
        tokensResponse.success &&
        tokensResponse.data?.total?.total_tokens > 0
      ) {
        processTokenData(tokensResponse.data)
        setTotalTokens({
          input: tokensResponse.data.total.input_tokens,
          output: tokensResponse.data.total.output_tokens,
          total: tokensResponse.data.total.total_tokens
        })
      } else {
        setTotalTokens({ input: 0, output: 0, total: 0 })
        processTokenData(tokensResponse.data)
        setTokenData([])
      }

      // SESSION TIME
      if (
        sessionResponse.success &&
        sessionResponse.data?.total?.avg_user_session_time_min > 0
      ) {
        processSessionData(sessionResponse.data)
        setTotalAvgSession(sessionResponse.data.total.avg_user_session_time_min)
      } else {
        setTotalAvgSession(0)
        processSessionData(sessionResponse.data)
      }

      // RESPONSE TIME
      if (
        responseResponse.success &&
        responseResponse.data?.total?.total_avg_response_time > 0
      ) {
        processResponseData(responseResponse.data)
        setTotalAvgResponse(responseResponse.data.total.total_avg_response_time)
      } else {
        setTotalAvgResponse(0)
        processResponseData(responseResponse.data)
      }

      // MESSAGES
      if (
        messagesResponse?.success &&
        messagesResponse.data?.result?.length > 0
      ) {
        setTotalMessagescount(messagesResponse.data.result[0])
      } else {
        setTotalMessagescount({})
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  useEffect(() => {
    fetchData(startDate, endDate)
  }, [])

  const processSessionData = apiData => {
    const data = apiData?.result || []

    const from = apiData?.date_range?.from
      ? new Date(apiData.date_range.from)
      : null
    const to = apiData?.date_range?.to ? new Date(apiData.date_range.to) : null

    if (!from || !to) return

    // Map session time per exact date
    const sessionMap = data.reduce((acc, item) => {
      acc[item.date] = item.avg_user_session_time_min || 0
      return acc
    }, {})

    const chartPoints = []
    let current = new Date(from)
    let index = 0

    while (current <= to) {
      const dateStr = current.toISOString().split('T')[0]
      chartPoints.push({
        name: current.toLocaleString('default', {
          month: 'short',
          day: 'numeric'
        }),
        minutes: sessionMap[dateStr] || 0,
        fullDate: dateStr,
        date: new Date(current),
        index: index++,
        hoverDate: current.toLocaleString('default', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      })
      current.setDate(current.getDate() + 1)
    }

    setAvgSessionData(chartPoints)
  }

  const processResponseData = data => {
    const result = data?.result || []
    const from = data?.date_range?.from ? new Date(data.date_range.from) : null
    const to = data?.date_range?.to ? new Date(data.date_range.to) : null

    if (!from || !to) return

    // Map response times by date string
    const responseMap = result.reduce((acc, item) => {
      acc[item.date] = item.avg_response_time_sec || 0
      return acc
    }, {})

    const chartPoints = []
    let current = new Date(from)
    let index = 0

    while (current <= to) {
      const dateStr = current.toISOString().split('T')[0]
      chartPoints.push({
        name: current.toLocaleString('default', {
          month: 'short',
          day: 'numeric'
        }),
        minutes: responseMap[dateStr] || 0,
        fullDate: dateStr,
        date: new Date(current),
        index: index++,
        hoverDate: current.toLocaleString('default', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      })
      current.setDate(current.getDate() + 1)
    }

    setAvgResponseData(chartPoints)
  }

  const processChartData = data => {
    if (!data || !data.result || data.result.length === 0) return

    const result = data.result
    const from = new Date(data.date_range.from)
    const to = new Date(data.date_range.to)
    const dayDiff = Math.ceil((to - from) / (1000 * 60 * 60 * 24))

    const dataMap = result.reduce((acc, item) => {
      acc[item.date] = (acc[item.date] || 0) + (item.new_users || 0)
      return acc
    }, {})

    const chartPoints = []
    const current = new Date(from)

    let index = 0
    while (current <= to) {
      const dateStr = current.toISOString().split('T')[0]
      chartPoints.push({
        name: current.toLocaleString('default', {
          month: 'short',
          day: 'numeric'
        }),
        fullDate: dateStr,
        users: dataMap[dateStr] || 0,
        date: new Date(current),
        index: index++,
        hoverDate: current.toLocaleString('default', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      })
      current.setDate(current.getDate() + 1)
    }

    setChartData(chartPoints)
    setYearRange(formatDateRange(from, to))
  }

  // Helper function to format date range as "May 19-Jun 17, 2025"
  const formatDateRange = (startDate, endDate) => {
    const startMonth = startDate.toLocaleString('default', { month: 'short' })
    const startDay = startDate.getDate()
    const endMonth = endDate.toLocaleString('default', { month: 'short' })
    const endDay = endDate.getDate()
    const year = endDate.getFullYear()

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}-${endDay}, ${year}`
    } else {
      return `${startMonth} ${startDay}-${endMonth} ${endDay}, ${year}`
    }
  }

  const processTokenData = data => {
    if (!data || !data.result || data.result.length === 0) return

    const usageData = data.result
    const from = new Date(data.date_range.from)
    const to = new Date(data.date_range.to)
    const dayDiff = Math.ceil((to - from) / (1000 * 60 * 60 * 24))

    const generatedDates = []
    let current = new Date(from)
    let index = 0

    // Generate the time range buckets
    if (dayDiff <= 60) {
      while (current <= to) {
        generatedDates.push({
          key: current.toISOString().split('T')[0],
          label: current.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          }),
          total: 0,
          index: index++
        })
        current.setDate(current.getDate() + 1)
      }
    } else if (dayDiff <= 365) {
      current = new Date(from.getFullYear(), from.getMonth(), 1)
      const end = new Date(to.getFullYear(), to.getMonth(), 1)
      while (current <= end) {
        const key = `${current.getFullYear()}-${String(
          current.getMonth() + 1
        ).padStart(2, '0')}-01`
        generatedDates.push({
          key,
          label: current.toLocaleDateString('en-US', { month: 'short' }),
          total: 0,
          index: index++
        })
        current.setMonth(current.getMonth() + 1)
      }
    } else {
      current = new Date(from.getFullYear(), from.getMonth(), 1)
      const end = new Date(to.getFullYear(), to.getMonth(), 1)
      while (current <= end) {
        const key = `${current.getFullYear()}-${String(
          current.getMonth() + 1
        ).padStart(2, '0')}-01`
        generatedDates.push({
          key,
          label: current.toLocaleDateString('en-US', { month: 'short' }),
          total: 0,
          index: index++
        })
        current.setMonth(current.getMonth() + 2)
      }
    }

    // Map total tokens per date or month
    const tokenMap = {}
    usageData.forEach(item => {
      const date = new Date(item.date)
      let key
      if (dayDiff <= 60) {
        key = item.date
      } else {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          '0'
        )}-01`
      }

      if (!tokenMap[key]) {
        tokenMap[key] = { total: 0 }
      }
      tokenMap[key].total += item.total_tokens || 0
    })

    const dailyData = generatedDates.map(entry => ({
      date: entry.label,
      total: tokenMap[entry.key]?.total || 0
    }))

    setTokenData(dailyData)
  }

  const formatDisplayDate = date => {
    return date?.toLocaleDateString('en-US', {
      weekday: 'short',
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    })
  }

  const handleSubmit = () => {
    if (!startDate || !endDate) {
      console.warn('Please select a valid date range')
      return
    }

    fetchData()
  }

  const handleClear = () => {
    const now = new Date()
    const lastMonth = new Date()
    lastMonth.setMonth(now.getMonth() - 1)
    const newRange = [lastMonth, now]
    setDateRange(newRange)
    setTempDateRange(newRange)
    fetchData(lastMonth, now)
  }

  useEffect(() => {
    console.log(limitedPermission, isRestricted, activePlan)
    // Simulate API delay or loading check
    const delay = setTimeout(() => {
      setLoading(false)
    }, 800) // adjust if needed (or remove if your subscription context already has a loading state)

    return () => clearTimeout(delay)
  }, [isRestricted, activePlan, limitedPermission])

  if (loading) {
    return (
      <div className='min-h-[29.1rem] h-full flex items-center justify-center'>
        <div className='text-center text-gray-500 animate-pulse'>
          <Loader />
        </div>
      </div>
    )
  }

  if (isRestricted) {
    return (
      <div className='min-h-[29.1rem] h-full relative'>
        <AccessRestrictedOverlay
          content={
            "Enable custom model training with proprietary datasets. Upgrade your plan to begin developing your organization's tailored AI solution."
          }
        />
      </div>
    )
  }

  return (
    <div className='p-6 bg-[#F8F8F8] min-h-screen'>
      <div className='flex flex-col mb-6'>
        <h1 className='font-extrabold text-[24px] leading-[48px]'>
          Session Overview
        </h1>
        {/* <p className='text-sm text-[#252530]'>Total User Session</p> */}
      </div>

      <div className='flex flex-col lg:flex-row items-start lg:items-center gap-4 mb-6'>
        {/* Trigger button with current selected range */}
        <div className='relative'>
          <div
            className='flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md cursor-pointer hover:border-gray-400'
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            <span className='text-xs'>Select Date</span>
            <span className='text-xs'>
              {startDate && endDate
                ? `${String(startDate.getDate()).padStart(2, '0')}/${String(
                    startDate.getMonth() + 1
                  ).padStart(2, '0')}/${startDate.getFullYear()} to ${String(
                    endDate.getDate()
                  ).padStart(2, '0')}/${String(endDate.getMonth() + 1).padStart(
                    2,
                    '0'
                  )}/${endDate.getFullYear()}`
                : 'Select a range'}
            </span>
          </div>

          {showDatePicker && (
            <div className='absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-20 '>
              {/* Header */}
              <div className='flex justify-between items-center border-b px-4 py-2'>
                <h3 className='text-sm font-semibold text-gray-700'>
                  Select Date Range
                </h3>
                <button
                  onClick={() => setShowDatePicker(false)}
                  aria-label='Close'
                >
                  <IonIcon
                    name='close-outline'
                    className='text-gray-500 hover:text-gray-700 text-[18px]'
                  />
                </button>
              </div>

              {/* Body */}
              <div className='flex flex-col lg:flex-row gap-6 items-start p-4'>
                {/* Left Panel: Inputs + Dropdown + Apply */}
                <div className='flex flex-col gap-3 w-full lg:w-[250px]'>
                  {/* Start & End Inputs */}
                  <div className='flex gap-2'>
                    <input
                      type='text'
                      readOnly
                      value={
                        tempDateRange[0]
                          ? `${String(tempDateRange[0].getDate()).padStart(
                              2,
                              '0'
                            )}/${String(
                              tempDateRange[0].getMonth() + 1
                            ).padStart(
                              2,
                              '0'
                            )}/${tempDateRange[0].getFullYear()}`
                          : ''
                      }
                      className='border px-3 py-2 rounded-md w-full text-sm outline-none'
                    />

                    <input
                      type='text'
                      readOnly
                      value={
                        tempDateRange[1]
                          ? `${String(tempDateRange[1].getDate()).padStart(
                              2,
                              '0'
                            )}/${String(
                              tempDateRange[1].getMonth() + 1
                            ).padStart(
                              2,
                              '0'
                            )}/${tempDateRange[1].getFullYear()}`
                          : ''
                      }
                      className='border px-3 py-2 rounded-md w-full text-sm outline-none'
                    />
                  </div>

                  {/* Quick Select Dropdown */}
                  <select
                    className='border px-3 py-2 rounded-md w-full text-sm'
                    defaultValue=''
                    onChange={e => {
                      const days = predefinedRanges[e.target.value]
                      if (days) {
                        const end = new Date()
                        const start = new Date()
                        start.setDate(end.getDate() - days)
                        const range = [start, end]
                        setTempDateRange(range)
                        setDateRange(range)
                      }
                    }}
                  >
                    <option value='' disabled>
                      Quick Select
                    </option>
                    <option value='1 Month'>Last 1 Month</option>
                    <option value='6 Months'>Last 6 Months</option>
                    <option value='12 Months'>Last 12 Months</option>
                  </select>

                  {/* Apply Button */}
                  <button
                    className='bg-[#232328] rounded-[59px] text-white py-2 px-7 transition-colors w-full text-sm'
                    onClick={() => {
                      setDateRange(tempDateRange)
                      setShowDatePicker(false)
                    }}
                  >
                    Apply
                  </button>
                </div>

                {/* Right Panel: Date Picker */}
                <div>
                  <DatePicker
                    selected={tempDateRange[0]}
                    onChange={update => setTempDateRange(update)}
                    startDate={tempDateRange[0]}
                    endDate={tempDateRange[1]}
                    selectsRange
                    inline
                    closeOnScroll
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          className='bg-[#232328] rounded-[59px] text-white py-2 px-7 transition'
          onClick={handleSubmit}
        >
          <p className='text-sm'>Submit</p>
        </button>

        {/* Clear Button */}
        <button
          onClick={handleClear}
          className='bg-gray-200 text-gray-700 rounded-[59px] py-2 px-7 transition hover:bg-gray-300'
        >
          <p className='text-sm'>Clear</p>
        </button>
      </div>

      {/* Metric Cards */}
      <div className='grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6'>
        <Card
          title='Total Messages'
          value={totalMessagescount.total_messages}
          type='message'
          iconbgcolor={'#EEF2FF'}
        />
        <Card
          title='Total Users'
          value={totalMessagescount.total_users}
          type='users'
          iconbgcolor={'#F0FDF4'}
          bordercolor={'#22C55E'}
        />
        <Card
          title='AI Messages'
          value={totalMessagescount.total_ai_messages}
          type='ai'
          iconbgcolor={'#FEFCE8'}
          bordercolor={'#F59E0B'}
        />

        <Card
          title='Conversation Flow'
          value={totalMessagescount.total_conversation_flow}
          type='mic'
          bordercolor={'#475569'}
        />
        <Card
          title='Total Tickets'
          value={totalMessagescount.total_ticket_messages}
          type='flow'
          bordercolor={'#475569'}
        />
      </div>

      {/* Charts Section */}
      <div className='w-full flex flex-col gap-6'>
        {/* Row 1: New Users & Token Usage */}
        <div className='w-full flex flex-col lg:flex-row gap-6'>
          {/* New Users Chart */}
          <div className='w-full lg:w-1/2 min-h-[300px]'>
            <div className='bg-white rounded-md shadow-sm h-full  flex flex-col justify-between'>
              <div className='border-b-[1px] p-4 flex justify-between items-center'>
                <h4 className='text-lg font-bold'>New Users</h4>
              </div>
              <div className='flex-1 flex flex-col mt-2 px-4'>
                <div className='flex-1 flex h-full w-full items-center justify-center'>
                  <div className='h-[230px] w-full overflow-x-auto no-scrollbar'>
                    <div className='min-w-[600px] h-full'>
                      {chartData.length === 0 ||
                      chartData.every(item => item.users === 0) ? (
                        <div className='h-full w-full flex items-center justify-center text-gray-400'>
                          No data available for selected range
                        </div>
                      ) : (
                        <ResponsiveContainer width='100%' height='100%'>
                          <LineChart
                            data={chartData}
                            margin={{
                              top: 5,
                              right: 12,
                              left: 5,
                              bottom: 20
                            }}
                          >
                            <CartesianGrid
                              strokeDasharray='3 3'
                              vertical={false}
                              stroke='#E5E7EB'
                            />
                            <XAxis
                              dataKey='name'
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 12 }}
                              padding={{ left: 10, right: 10 }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 12 }}
                              domain={[0, 'dataMax + 25']}
                              tickCount={5}
                              label={{
                                value: 'Number of Users',
                                angle: -90,
                                position: 'outsideLeft',
                                fontSize: 12,
                                fill: '#6B7280',
                                dx: -10
                              }}
                            />
                            <Tooltip
                              formatter={value => [`${value}`, 'Users']}
                              labelFormatter={(label, payload) => {
                                const fullDate = payload?.[0]?.payload?.fullDate
                                return fullDate || label
                              }}
                              contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #E5E7EB',
                                borderRadius: '6px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                zIndex: 3000
                              }}
                            />

                            <Line
                              type='monotone'
                              dataKey='users'
                              stroke='#6C63FF'
                              strokeWidth={3}
                              dot={({ cx, cy, payload }) =>
                                payload.users > 0 ? (
                                  <circle
                                    cx={cx}
                                    cy={cy}
                                    r={4}
                                    stroke='#000'
                                    strokeWidth={2}
                                    fill='#000'
                                  />
                                ) : null
                              }
                              activeDot={({ cx, cy, payload }) =>
                                payload.users > 0 ? (
                                  <circle
                                    cx={cx}
                                    cy={cy}
                                    r={6}
                                    stroke='#000'
                                    strokeWidth={2}
                                    fill='#000'
                                  />
                                ) : null
                              }
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className='text-xs text-gray-500 text-center p-4 border-t-[1px]'>
                Month, Year
              </div>
            </div>
          </div>

          {/* Token Usage Chart */}
          <div className='w-full lg:w-1/2 min-h-[300px]'>
            <div className='bg-white rounded-md shadow-sm h-full flex flex-col justify-between p-4'>
              <div>
                <div className='flex flex-col mb-2'>
                  <h4 className='font-bold text-[18px] mb-4'>Token Usage</h4>
                  <p className='text-2xl font-medium mb-2'>
                    {totalTokens.total}
                  </p>
                </div>
              </div>
              <div className='flex-1 flex flex-col mt-2 px-4'>
                <div className='flex-1 flex h-full w-full items-center justify-center'>
                  <div className='h-[230px] w-full overflow-x-auto no-scrollbar'>
                    <div className='min-w-[600px] h-full'>
                      {tokenData.length === 0 ||
                      tokenData.every(item => (item.total || 0) === 0) ? (
                        <div className='h-full w-full flex items-center justify-center text-gray-400'>
                          No token data available for selected range
                        </div>
                      ) : (
                        <ResponsiveContainer width='100%' height='100%'>
                          <BarChart
                            data={tokenData}
                            margin={{
                              top: 5,
                              right: 12,
                              left: 5,
                              bottom: 20
                            }}
                            barSize={80}
                          >
                            <CartesianGrid
                              strokeDasharray='5 3'
                              stroke='#E5E7EB'
                              vertical={true}
                              horizontal={true}
                            />
                            <XAxis
                              dataKey='date'
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 12 }}
                            />
                            <Tooltip
                              formatter={value => [
                                `${value} tokens`,
                                'Total Tokens'
                              ]}
                            />
                            <Legend
                              content={() => (
                                <div className='flex gap-2 items-center mt-2 ml-8 text-sm text-gray-700'>
                                  <span className='inline-block w-3 h-3 rounded-full bg-[#6C63FF]' />
                                  Total Tokens
                                </div>
                              )}
                            />
                            <Bar
                              dataKey='total'
                              fill='#6C63FF'
                              name='Total Tokens'
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Avg Response Time & Avg Session Time */}
        <div className='w-full flex flex-col lg:flex-row gap-6'>
          {/* Average Response Time Chart */}
          <div className='w-full lg:w-1/2 min-h-[300px]'>
            <div className='bg-white rounded-md shadow-sm h-full flex flex-col justify-between'>
              {/* Header */}
              <div className='p-4 flex flex-col gap-3'>
                <h4 className='text-lg font-bold'>Average Response Time</h4>
                <div className='flex gap-2 items-end'>
                  <p className='text-2xl font-medium leading-7'>
                    {totalAvgResponse.toFixed(2)}
                  </p>

                  {totalAvgResponse >= 1 ? (
                    <div className='flex items-end gap-1 text-sm'>
                      <span>Sec</span>
                      <IonIcon
                        name='trending-up-outline'
                        className='text-green-500 text-base mb-[2px]'
                      />
                    </div>
                  ) : (
                    <div className='flex items-end gap-1 text-sm'>
                      <span>Sec</span>
                      <IonIcon
                        name='trending-down-outline'
                        className='text-red-500 text-base mb-[2px]'
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Chart */}
              <div className='flex-1 flex flex-col mt-2 px-4'>
                <div className='flex-1 flex h-full w-full items-center justify-center'>
                  <div className='h-[230px] w-full overflow-x-auto no-scrollbar'>
                    <div className='min-w-[600px] h-full'>
                      {avgResponseData.length === 0 ||
                      avgResponseData.every(
                        item => (item.minutes || 0) === 0
                      ) ? (
                        <div className='h-full w-full flex items-center justify-center text-gray-400'>
                          No average response data available for selected range
                        </div>
                      ) : (
                        <ResponsiveContainer width='100%' height='100%'>
                          <LineChart
                            data={avgResponseData}
                            margin={{
                              top: 5,
                              right: 12,
                              left: 5,
                              bottom: 20
                            }}
                          >
                            <CartesianGrid
                              strokeDasharray='3 3'
                              vertical={false}
                              stroke='#E5E7EB'
                            />
                            <XAxis
                              dataKey='name'
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 12 }}
                              padding={{ left: 10, right: 10 }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={false}
                              padding={{ left: 10 }}
                            />
                            <Tooltip
                              formatter={value => [`${value}`, 'Seconds']}
                              labelFormatter={(label, payload) => {
                                const hoverDate =
                                  payload?.[0]?.payload?.hoverDate
                                return hoverDate || label
                              }}
                              contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #E5E7EB',
                                borderRadius: '6px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                zIndex: 10000
                              }}
                            />
                            <Line
                              type='monotone'
                              dataKey='minutes'
                              stroke='#6C63FF'
                              strokeWidth={3}
                              dot={({ cx, cy, payload }) =>
                                payload.minutes > 0 ? (
                                  <circle
                                    cx={cx}
                                    cy={cy}
                                    r={4}
                                    stroke='#000'
                                    strokeWidth={2}
                                    fill='#000'
                                  />
                                ) : null
                              }
                              activeDot={({ cx, cy, payload }) =>
                                payload.minutes > 0 ? (
                                  <circle
                                    cx={cx}
                                    cy={cy}
                                    r={6}
                                    stroke='#000'
                                    strokeWidth={2}
                                    fill='#000'
                                  />
                                ) : null
                              }
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Average Session Time Chart */}
          <div className='w-full lg:w-1/2 min-h-[300px]'>
            <div className='bg-white rounded-md shadow-sm h-full flex flex-col justify-between'>
              <div className='p-4 flex flex-col gap-3'>
                <h4 className='text-lg font-bold'>Average Session Time</h4>
                <div className='flex gap-2 items-end'>
                  <p className='text-2xl font-medium leading-7'>
                    {totalAvgSession.toFixed(2)}
                  </p>
                  {totalAvgSession > 1 ? (
                    <div className='flex items-end gap-1 text-sm'>
                      <span>{totalAvgSession >= 1 ? 'min' : 'seconds'}</span>
                      <IonIcon
                        name='trending-up-outline'
                        className='text-green-500 text-base mb-[2px]'
                      />
                    </div>
                  ) : (
                    <div className='flex items-end gap-1 text-sm text-gray-500'>
                      <span>min</span>
                      <IonIcon
                        name='trending-down-outline'
                        className='text-red-500 text-base mb-[2px]'
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className='flex-1 flex flex-col mt-2 px-4'>
                <div className='flex-1 flex h-full w-full items-center justify-center'>
                  <div className='h-[230px] w-full overflow-x-auto no-scrollbar'>
                    <div className='min-w-[600px] h-full'>
                      {avgSessionData.length === 0 ||
                      avgSessionData.every(
                        item => (item.minutes || 0) === 0
                      ) ? (
                        <div className='h-full w-full flex items-center justify-center text-gray-400'>
                          No average session data available for selected range
                        </div>
                      ) : (
                        <ResponsiveContainer width='100%' height='100%'>
                          <LineChart
                            data={avgSessionData}
                            margin={{
                              top: 5,
                              right: 12,
                              left: 5,
                              bottom: 20
                            }}
                          >
                            <CartesianGrid
                              strokeDasharray='3 3'
                              vertical={false}
                              stroke='#E5E7EB'
                            />
                            <XAxis
                              dataKey='name'
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 12 }}
                              padding={{ left: 10, right: 10 }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={false}
                              padding={{ left: 10, top: 5 }}
                            />
                            <Tooltip
                              formatter={value => [`${value}`, 'Minutes']}
                              labelFormatter={(label, payload) => {
                                // Simply return the fullDate if it exists, otherwise return the label
                                return payload?.[0]?.payload?.fullDate || label
                              }}
                              contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #E5E7EB',
                                borderRadius: '6px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                zIndex: 10000
                              }}
                            />
                            <Line
                              type='monotone'
                              dataKey='minutes'
                              stroke='#10B981'
                              strokeWidth={3}
                              dot={({ cx, cy, payload }) =>
                                payload.minutes > 0 ? (
                                  <circle
                                    cx={cx}
                                    cy={cy}
                                    r={4}
                                    stroke='#000'
                                    strokeWidth={2}
                                    fill='#000'
                                  />
                                ) : null
                              }
                              activeDot={({ cx, cy, payload }) =>
                                payload.minutes > 0 ? (
                                  <circle
                                    cx={cx}
                                    cy={cy}
                                    r={6}
                                    stroke='#000'
                                    strokeWidth={2}
                                    fill='#000'
                                  />
                                ) : null
                              }
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Icon + Color maps
const iconMap = {
  message: <IonIcon name='chatbox-ellipses' className='text-[#5D5FEF]' />,
  users: <IonIcon className='text-[#00B894]' name='people' />,
  ai: <Sparkles className='text-[#F6B40A]' />,
  //   flow: (
  //     <svg
  //       width='23'
  //       height='23'
  //       viewBox='0 0 21 21'
  //       fill='none'
  //       xmlns='http://www.w3.org/2000/svg'
  //     >
  //       <path
  //         fillRule='evenodd'
  //         clipRule='evenodd'
  //         d='M7.5 16.8163C7.91421 16.8163 8.25 17.1521 8.25 17.5663V19.8163C8.25 20.2305 7.91421 20.5663 7.5 20.5663C7.08579 20.5663 6.75 20.2305 6.75 19.8163V17.5663C6.75 17.1521 7.08579 16.8163 7.5 16.8163Z'
  //         fill='#475569'
  //       />
  //       <path
  //         fillRule='evenodd'
  //         clipRule='evenodd'
  //         d='M4.84846 15.7179C5.14135 16.0108 5.14135 16.4856 4.84845 16.7785L3.25752 18.3695C2.96462 18.6624 2.48975 18.6624 2.19686 18.3695C1.90396 18.0766 1.90396 17.6017 2.19686 17.3088L3.78779 15.7179C4.08069 15.425 4.55556 15.425 4.84846 15.7179Z'
  //         fill='#475569'
  //       />
  //       <path
  //         fillRule='evenodd'
  //         clipRule='evenodd'
  //         d='M0 13.0663C0 12.6521 0.335786 12.3163 0.75 12.3163H3C3.41421 12.3163 3.75 12.6521 3.75 13.0663C3.75 13.4805 3.41421 13.8163 3 13.8163H0.75C0.335786 13.8163 0 13.4805 0 13.0663Z'
  //         fill='#475569'
  //       />
  //       <path
  //         d='M11.0428 12.7162L9.58453 14.1744C9.51487 14.2441 9.45961 14.3268 9.42191 14.4178C9.38421 14.5088 9.3648 14.6063 9.3648 14.7048C9.3648 14.8033 9.38421 14.9009 9.42191 14.9919C9.45961 15.0829 9.51487 15.1656 9.58453 15.2352L11.7061 17.3563C11.8467 17.4969 12.0374 17.5758 12.2362 17.5758C12.4351 17.5758 12.6258 17.4969 12.7664 17.3563L14.2252 15.898C14.2426 15.8806 14.2564 15.86 14.2659 15.8372C14.2753 15.8144 14.2801 15.79 14.2801 15.7654C14.2801 15.7407 14.2753 15.7163 14.2659 15.6936C14.2564 15.6708 14.2426 15.6501 14.2252 15.6327L11.3081 12.7162C11.2907 12.6987 11.27 12.6849 11.2473 12.6755C11.2245 12.666 11.2001 12.6612 11.1755 12.6612C11.1508 12.6612 11.1264 12.666 11.1037 12.6755C11.0809 12.6849 11.0602 12.6987 11.0428 12.7162Z'
  //         fill='#475569'
  //       />
  //       <path
  //         d='M4.67906 6.35195L3.22078 7.8107C3.08024 7.95134 3.00129 8.14203 3.00129 8.34086C3.00129 8.53968 3.08024 8.73037 3.22078 8.87101L5.34188 10.9926C5.41152 11.0622 5.49421 11.1175 5.58521 11.1552C5.67622 11.1929 5.77376 11.2123 5.87227 11.2123C5.97077 11.2123 6.06831 11.1929 6.15932 11.1552C6.25032 11.1175 6.33301 11.0622 6.40266 10.9926L7.86094 9.53429C7.87837 9.51688 7.8922 9.4962 7.90164 9.47344C7.91107 9.45068 7.91593 9.42628 7.91593 9.40164C7.91593 9.377 7.91107 9.3526 7.90164 9.32984C7.8922 9.30707 7.87837 9.2864 7.86094 9.26898L4.94438 6.35195C4.92696 6.33452 4.90628 6.32069 4.88352 6.31125C4.86076 6.30182 4.83636 6.29696 4.81172 6.29696C4.78708 6.29696 4.76268 6.30182 4.73992 6.31125C4.71716 6.32069 4.69648 6.33452 4.67906 6.35195Z'
  //         fill='#475569'
  //       />
  //       <path
  //         d='M18.6033 1.97992C17.9766 1.35249 17.2325 0.854699 16.4133 0.514976C15.5942 0.175253 14.7162 0.00026148 13.8294 2.92814e-07C12.9426 -0.000260895 12.0645 0.174213 11.2452 0.513454C10.4259 0.852694 9.68141 1.35005 9.05437 1.97711L6.00469 5.02633C5.98725 5.04374 5.97342 5.06442 5.96399 5.08718C5.95455 5.10994 5.9497 5.13434 5.9497 5.15898C5.9497 5.18362 5.95455 5.20802 5.96399 5.23078C5.97342 5.25354 5.98725 5.27422 6.00469 5.29164L8.92172 8.2082C8.93913 8.22563 8.95981 8.23946 8.98257 8.2489C9.00534 8.25833 9.02973 8.26319 9.05437 8.26319C9.07902 8.26319 9.10341 8.25833 9.12618 8.2489C9.14894 8.23946 9.16962 8.22563 9.18703 8.2082L12.2339 5.15898C12.4451 4.94776 12.6963 4.78071 12.9728 4.66759C13.2493 4.55447 13.5455 4.49753 13.8442 4.50011C14.1429 4.50268 14.4382 4.56471 14.7126 4.68258C14.9871 4.80046 15.2354 4.9718 15.443 5.18664C16.3008 6.07351 16.2619 7.49476 15.3895 8.36711L12.3689 11.3887C12.3515 11.4061 12.3376 11.4268 12.3282 11.4495C12.3188 11.4723 12.3139 11.4967 12.3139 11.5213C12.3139 11.546 12.3188 11.5704 12.3282 11.5931C12.3376 11.6159 12.3515 11.6366 12.3689 11.654L15.2855 14.571C15.3029 14.5884 15.3236 14.6023 15.3463 14.6117C15.3691 14.6211 15.3935 14.626 15.4181 14.626C15.4428 14.626 15.4672 14.6211 15.4899 14.6117C15.5127 14.6023 15.5334 14.5884 15.5508 14.571L18.5545 11.5673C21.1969 8.92632 21.2437 4.62367 18.6033 1.97992Z'
  //         fill='#475569'
  //       />
  //     </svg>
  //   ),
  mic: (
    <svg
      width='21'
      height='21'
      viewBox='0 0 21 21'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className=''
    >
      <path
        fill-rule='evenodd'
        clip-rule='evenodd'
        d='M7.5 16.8163C7.91421 16.8163 8.25 17.1521 8.25 17.5663V19.8163C8.25 20.2305 7.91421 20.5663 7.5 20.5663C7.08579 20.5663 6.75 20.2305 6.75 19.8163V17.5663C6.75 17.1521 7.08579 16.8163 7.5 16.8163Z'
        fill='#475569'
      />
      <path
        fill-rule='evenodd'
        clip-rule='evenodd'
        d='M4.84846 15.7179C5.14135 16.0108 5.14135 16.4856 4.84845 16.7785L3.25752 18.3695C2.96462 18.6624 2.48975 18.6624 2.19686 18.3695C1.90396 18.0766 1.90396 17.6017 2.19686 17.3088L3.78779 15.7179C4.08069 15.425 4.55556 15.425 4.84846 15.7179Z'
        fill='#475569'
      />
      <path
        fill-rule='evenodd'
        clip-rule='evenodd'
        d='M0 13.0663C0 12.6521 0.335786 12.3163 0.75 12.3163H3C3.41421 12.3163 3.75 12.6521 3.75 13.0663C3.75 13.4805 3.41421 13.8163 3 13.8163H0.75C0.335786 13.8163 0 13.4805 0 13.0663Z'
        fill='#475569'
      />
      <path
        d='M11.0428 12.7162L9.58453 14.1744C9.51487 14.2441 9.45961 14.3268 9.42191 14.4178C9.38421 14.5088 9.3648 14.6063 9.3648 14.7048C9.3648 14.8033 9.38421 14.9009 9.42191 14.9919C9.45961 15.0829 9.51487 15.1656 9.58453 15.2352L11.7061 17.3563C11.8467 17.4969 12.0374 17.5758 12.2362 17.5758C12.4351 17.5758 12.6258 17.4969 12.7664 17.3563L14.2252 15.898C14.2426 15.8806 14.2564 15.86 14.2659 15.8372C14.2753 15.8144 14.2801 15.79 14.2801 15.7654C14.2801 15.7407 14.2753 15.7163 14.2659 15.6936C14.2564 15.6708 14.2426 15.6501 14.2252 15.6327L11.3081 12.7162C11.2907 12.6987 11.27 12.6849 11.2473 12.6755C11.2245 12.666 11.2001 12.6612 11.1755 12.6612C11.1508 12.6612 11.1264 12.666 11.1037 12.6755C11.0809 12.6849 11.0602 12.6987 11.0428 12.7162Z'
        fill='#475569'
      />
      <path
        d='M4.67906 6.35195L3.22078 7.8107C3.08024 7.95134 3.00129 8.14203 3.00129 8.34086C3.00129 8.53968 3.08024 8.73037 3.22078 8.87101L5.34188 10.9926C5.41152 11.0622 5.49421 11.1175 5.58521 11.1552C5.67622 11.1929 5.77376 11.2123 5.87227 11.2123C5.97077 11.2123 6.06831 11.1929 6.15932 11.1552C6.25032 11.1175 6.33301 11.0622 6.40266 10.9926L7.86094 9.53429C7.87837 9.51688 7.8922 9.4962 7.90164 9.47344C7.91107 9.45068 7.91593 9.42628 7.91593 9.40164C7.91593 9.377 7.91107 9.3526 7.90164 9.32984C7.8922 9.30707 7.87837 9.2864 7.86094 9.26898L4.94438 6.35195C4.92696 6.33452 4.90628 6.32069 4.88352 6.31125C4.86076 6.30182 4.83636 6.29696 4.81172 6.29696C4.78708 6.29696 4.76268 6.30182 4.73992 6.31125C4.71716 6.32069 4.69648 6.33452 4.67906 6.35195Z'
        fill='#475569'
      />
      <path
        d='M18.6033 1.97992C17.9766 1.35249 17.2325 0.854699 16.4133 0.514976C15.5942 0.175253 14.7162 0.00026148 13.8294 2.92814e-07C12.9426 -0.000260895 12.0645 0.174213 11.2452 0.513454C10.4259 0.852694 9.68141 1.35005 9.05437 1.97711L6.00469 5.02633C5.98725 5.04374 5.97342 5.06442 5.96399 5.08718C5.95455 5.10994 5.9497 5.13434 5.9497 5.15898C5.9497 5.18362 5.95455 5.20802 5.96399 5.23078C5.97342 5.25354 5.98725 5.27422 6.00469 5.29164L8.92172 8.2082C8.93913 8.22563 8.95981 8.23946 8.98257 8.2489C9.00534 8.25833 9.02973 8.26319 9.05437 8.26319C9.07902 8.26319 9.10341 8.25833 9.12618 8.2489C9.14894 8.23946 9.16962 8.22563 9.18703 8.2082L12.2339 5.15898C12.4451 4.94776 12.6963 4.78071 12.9728 4.66759C13.2493 4.55447 13.5455 4.49753 13.8442 4.50011C14.1429 4.50268 14.4382 4.56471 14.7126 4.68258C14.9871 4.80046 15.2354 4.9718 15.443 5.18664C16.3008 6.07351 16.2619 7.49476 15.3895 8.36711L12.3689 11.3887C12.3515 11.4061 12.3376 11.4268 12.3282 11.4495C12.3188 11.4723 12.3139 11.4967 12.3139 11.5213C12.3139 11.546 12.3188 11.5704 12.3282 11.5931C12.3376 11.6159 12.3515 11.6366 12.3689 11.654L15.2855 14.571C15.3029 14.5884 15.3236 14.6023 15.3463 14.6117C15.3691 14.6211 15.3935 14.626 15.4181 14.626C15.4428 14.626 15.4672 14.6211 15.4899 14.6117C15.5127 14.6023 15.5334 14.5884 15.5508 14.571L18.5545 11.5673C21.1969 8.92632 21.2437 4.62367 18.6033 1.97992Z'
        fill='#475569'
      />
    </svg>
  ),
  flow: <TicketIcon className='text-[#475569]' />
}

const Card = ({
  title,
  value,
  type,
  active = false,
  iconbgcolor = '#F5F6FA',
  bordercolor = ''
}) => {
  const icon = iconMap[type] || <MessageCircle />

  return (
    <div className='bg-white rounded-xl px-6 py-4 flex flex-col items-start justify-center shadow-sm text-center transition'>
      <div
        className='rounded-md p-2 text-[22px] border-[1px] mb-3 flex items-center justify-center'
        style={{
          backgroundColor: iconbgcolor,
          borderColor: bordercolor
        }}
      >
        {icon}
      </div>

      <p className='text-[13px] font-medium text-[#252530] mb-1'>{title}</p>
      <h3 className='text-[15px] font-semibold'>{value}</h3>
    </div>
  )
}

export default Reports
